import React, { useState, useEffect } from 'react';
import './Module.css';
import Network from '../../../models/Network.js';
import { SUBMIT_DEMO_DATA_URL, CONVERT_XML_TO_JSON_URL, UPDATE_DEMO_CODE_USAGE_URL } from '../../../urls.js';
import xpath from 'xpath';
import { DOMParser } from '@xmldom/xmldom';
import { XMLSerializer } from '@xmldom/xmldom';
import AccordionListElement from '../list_elements/AccordionListElement.js';
import ErrorListElement from '../list_elements/ErrorListElement.js';

const DemoReportEditModule = ({ 
    reportXml: initialReportXml, 
    onBack, 
    onAccessFeedback, 
    onXmlUpdate, 
    sessionAgency, 
    onUsageUpdate,
    onReportSubmitted,
    hasSubmittedReport,
    reportId: externalReportId,
    codeData
}) => {
    const [isProcessing, setIsProcessing] = useState(false);
    const [reportId, setReportId] = useState(externalReportId || null);
    const [issuesCount, setIssuesCount] = useState(null);
    const [suggestionsCount, setSuggestionsCount] = useState(null);
    const [schematronCount, setSchematronCount] = useState(null);
    const [error, setError] = useState(null);
    const [reportXml, setReportXml] = useState(initialReportXml);
    const [elapsedTime, setElapsedTime] = useState(0);
    const [timerInterval, setTimerInterval] = useState(null);
    const [narrative, setNarrative] = useState('');
    const [editedNarrative, setEditedNarrative] = useState('');
    const [jsonResult, setJsonResult] = useState(null);
    const [isConvertingToJson, setIsConvertingToJson] = useState(false);

    // Check if user has reached demo usage limit
    const hasReachedUsageLimit = codeData && (codeData.number_uses || 0) >= 8;

    // Update internal reportId when external reportId changes
    useEffect(() => {
        if (externalReportId) {
            setReportId(externalReportId);
        }
    }, [externalReportId]);

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const updateNarrativeInXml = () => {
        try {
            const doc = new DOMParser().parseFromString(reportXml, 'text/xml');
            const nodes = xpath.select("//*[local-name()='eNarrative.01']", doc);
            
            if (nodes.length > 0) {
                // Update the narrative content
                nodes[0].firstChild.data = editedNarrative;
                
                // Convert the modified document back to string
                const serializer = new XMLSerializer();
                const modifiedXml = serializer.serializeToString(doc);
                setReportXml(modifiedXml);
                setNarrative(editedNarrative);
                
                // Notify parent component of XML update
                if (onXmlUpdate) {
                    onXmlUpdate(modifiedXml);
                }
            }
        } catch (err) {
            console.error('Error updating XML:', err);
            setError('Error updating XML. Please check the format.');
        }
    };

    const handleSubmit = async () => {
        setIsProcessing(true);
        setReportId(null);
        setError(null);
        setElapsedTime(0);
        
        // Start the timer
        const interval = setInterval(() => {
            setElapsedTime(prev => prev + 1);
        }, 1000);
        setTimerInterval(interval);
        
        // Update eRecord.01 with current submission count before submitting
        let xmlToSubmit = reportXml;
        try {
            const doc = new DOMParser().parseFromString(reportXml, 'text/xml');
            const nodes = xpath.select("//*[local-name()='eRecord.01']", doc);
            
            if (nodes.length > 0) {
                // Get current submission count (add 1 since this will be the new submission)
                const currentCount = codeData ? (codeData.number_uses || 0) : 0;
                const newCount = currentCount + 1;
                
                // Update the eRecord.01 content
                nodes[0].firstChild.data = newCount.toString();
                
                // Convert the modified document back to string
                const serializer = new XMLSerializer();
                xmlToSubmit = serializer.serializeToString(doc);
            }
        } catch (err) {
            console.error('Error updating eRecord.01:', err);
            // Continue with submission even if eRecord.01 update fails
        }
        
        const dict = {
            report_xml: xmlToSubmit,
            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
        };

        await Network.post(SUBMIT_DEMO_DATA_URL, dict, (response) => {
            // Clear the timer
            clearInterval(timerInterval);
            setTimerInterval(null);
            
            if (Network.checkValidStatusAndResponse(response)) {
                if (response.data.status === 'success' && response.data.report_id) {
                    const newReportId = response.data.report_id;
                    setReportId(newReportId);
                    setIssuesCount(response.data.issues_count);
                    setSuggestionsCount(response.data.suggestions_count);
                    setSchematronCount(response.data.schematron_count);
                    
                    // Notify parent component that report was submitted
                    if (onReportSubmitted) {
                        onReportSubmitted(newReportId);
                    }
                    
                    // Update demo code usage if we have a session agency
                    if (sessionAgency && sessionAgency !== 'demo agency') {
                        const usageDict = {
                            demo_code: sessionAgency,
                            CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
                        };
                        
                        Network.post(UPDATE_DEMO_CODE_USAGE_URL, usageDict, (usageResponse) => {
                            if (Network.checkValidStatusAndResponse(usageResponse)) {
                                if (usageResponse.data.status === 'success' && onUsageUpdate) {
                                    onUsageUpdate(usageResponse.data.code_data);
                                }
                            }
                        });
                    }
                } else {
                    setError('Error: No report ID received');
                }
            } else {
                setError('Error submitting report. Please try again.');
            }
            setIsProcessing(false);
        });
    };

    // Auto-convert XML to JSON on component mount
    useEffect(() => {
        const autoConvertToJson = async () => {
            setIsConvertingToJson(true);
            setError(null);
            
            const dict = {
                xml_string: reportXml,
                CLIENT_ACCESS_KEY: process.env.REACT_APP_CLIENT_ACCESS_KEY
            };

            await Network.post(CONVERT_XML_TO_JSON_URL, dict, (response) => {
                if (Network.checkValidStatusAndResponse(response)) {
                    if (response.data.status === 'success' && response.data.json_data) {
                        // remove 'eNarrative' and 'eCustomResults' keys and children
                        delete response.data.json_data.EMSDataSet.Header.PatientCareReport.eNarrative;
                        delete response.data.json_data.EMSDataSet.Header.PatientCareReport.eCustomResults;
                        setJsonResult(response.data.json_data);
                    } else {
                        setError('Error: No JSON data received');
                    }
                } else {
                    setError('Error converting XML to JSON. Please try again.');
                }
                setIsConvertingToJson(false);
            });
        };

        autoConvertToJson();
    }, []); // Only run on mount

    // Cleanup timer on component unmount
    useEffect(() => {
        return () => {
            if (timerInterval) {
                clearInterval(timerInterval);
            }
        };
    }, [timerInterval]);

    useEffect(() => {
        try {
            const doc = new DOMParser().parseFromString(reportXml, 'text/xml');
            const nodes = xpath.select("//*[local-name()='dAgency.02']", doc);
            
            if (nodes.length > 0) {
                // Replace the content of each matching node
                nodes.forEach(node => {
                    node.firstChild.data = sessionAgency;
                });
                
                // Convert the modified document back to string
                const serializer = new XMLSerializer();
                const modifiedXml = serializer.serializeToString(doc);
                setReportXml(modifiedXml);
            }
        } catch (err) {
            console.error('Error processing XML:', err);
            setError('Error processing XML for agency number. Please check the format.');
        }
    }, [sessionAgency]);

    useEffect(() => {
        try {
            // get the value of narrative
            const doc = new DOMParser().parseFromString(reportXml, 'text/xml');
            const nodes = xpath.select("//*[local-name()='eNarrative.01']", doc);
            if (nodes.length > 0 && nodes[0].firstChild) {
                const narrativeText = nodes[0].firstChild.data;
                setNarrative(narrativeText);
                setEditedNarrative(narrativeText);
            }
        } catch (err) {
            console.error('Error processing XML:', err);
            setError('Error processing XML for narrative. Please check the format.');
        }
    }, []);

    // Determine if we should show the post-submission state
    const shouldShowPostSubmissionState = hasSubmittedReport || reportId;

    return (
        <div className="standard-module">
            <div style={{paddingBottom: '15px'}}>
                <h2>Review and Submit Report</h2>
                {!shouldShowPostSubmissionState && (
                    <p>Review and edit the below narrative and report. Submit the report when ready.</p>
                )}
                {/* {shouldShowPostSubmissionState && (
                    <p>Review and edit the below narrative and report. Submit the report when ready.</p>
                )} */}
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginBottom: '20px' }}>
                {!shouldShowPostSubmissionState && (
                    <button 
                        className="standard-button"
                        onClick={handleSubmit}
                        disabled={isProcessing || hasReachedUsageLimit}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: hasReachedUsageLimit ? '#ccc' : 'var(--blue)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: hasReachedUsageLimit ? 'not-allowed' : 'pointer'
                        }}
                    >
                        {hasReachedUsageLimit ? 'Submit' : 'Submit'}
                    </button>
                )}
            </div>

            {hasReachedUsageLimit && (
                <ErrorListElement 
                    errorMessage="Demo Usage Limit Reached: You have used all of your demo submissions. Please contact for additional access or to set up a full account."
                    height={60}
                />
            )}

            {isProcessing && (
                <div style={{ 
                    marginBottom: '20px', 
                    padding: '40px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '8px',
                    textAlign: 'center'
                }}>
                    <div style={{ 
                        display: 'inline-block',
                        width: '40px',
                        height: '40px',
                        border: '4px solid #f3f3f3',
                        borderTop: '4px solid var(--blue)',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite',
                        marginBottom: '20px'
                    }}></div>
                    <div style={{ fontSize: '18px', marginBottom: '10px', color: '#495057' }}>
                        Processing Report...
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                        Time elapsed: {formatTime(elapsedTime)}
                    </div>
                </div>
            )}

            {isConvertingToJson && (
                <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#f0f8ff', 
                    borderRadius: '5px',
                    textAlign: 'center'
                }}>
                    <p>Converting XML to JSON...</p>
                </div>
            )}

            {error && (
                <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#ffe6e6', 
                    borderRadius: '5px',
                    textAlign: 'center'
                }}>
                    <p>{error}</p>
                </div>
            )}

            {shouldShowPostSubmissionState && elapsedTime !== 0 && (
                <div style={{ 
                    marginBottom: '20px', 
                    padding: '15px', 
                    backgroundColor: '#e6ffe6', 
                    borderRadius: '5px',
                    textAlign: 'center'
                }}>
                    <p>Processing complete in {formatTime(elapsedTime)}</p>
                    <p>Suggestions: {suggestionsCount}, Issues: {issuesCount}</p>
                    <div style={{ marginTop: '15px' }}>
                        <p style={{ 
                            fontSize: '0.9em', 
                            color: '#666', 
                            marginBottom: '10px',
                            fontStyle: 'italic'
                        }}>
                            In actual use, the medic who authored the report will receive a secure link to access feedback in an email.
                        </p>
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <button 
                                className="standard-button"
                                onClick={() => onAccessFeedback(reportId)}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Access Feedback
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {!isProcessing && (
                <div style={{ marginBottom: '20px' }}>
                    {/* <h3 style={{ marginBottom: '10px' }}>Narrative</h3> */}
                    <textarea
                        value={editedNarrative}
                        onChange={(e) => setEditedNarrative(e.target.value)}
                        disabled={shouldShowPostSubmissionState}
                        style={{
                            width: '100%',
                            minHeight: '150px',
                            padding: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontFamily: 'monospace',
                            fontSize: '14px',
                            resize: 'vertical',
                            backgroundColor: shouldShowPostSubmissionState ? '#f5f5f5' : 'white',
                            cursor: shouldShowPostSubmissionState ? 'not-allowed' : 'text'
                        }}
                        placeholder="Enter narrative text..."
                    />
                    {!shouldShowPostSubmissionState && (
                        <div style={{ marginTop: '10px', textAlign: 'right' }}>
                            <button
                                className="standard-button"
                                onClick={updateNarrativeInXml}
                                disabled={editedNarrative === narrative}
                                style={{
                                    padding: '6px 12px',
                                    backgroundColor: editedNarrative === narrative ? '#ccc' : 'var(--blue)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: editedNarrative === narrative ? 'not-allowed' : 'pointer'
                                }}
                            >
                                Update Narrative
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* JSON Result at the bottom */}
            {jsonResult && !isProcessing && (
                <div>
                    {/* <h3>Report Data</h3> */}
                    <AccordionListElement reportJSON={jsonResult.EMSDataSet.Header.PatientCareReport} readOnly={true} />
                </div>
            )}
        </div>
    );
};

export default DemoReportEditModule; 